import { gql, useMutation, useQuery } from "@apollo/client";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import {
  ArrowPathIcon,
  BoltIcon,
  PaperAirplaneIcon,
  StarIcon,
  TrashIcon,
} from "@heroicons/react/24/solid";
import {
  Avatar,
  Badge as MuiBadge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { format } from "date-fns";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import EmptyState from "../../components/EmptyState/EmptyState";
import FilterChip from "../../components/FilterChip/FilterChip";
import Page from "../../components/Page/Page";
import Badge from "../../components/Styled/Badge";

const GET_AI_BOT_USERS = gql`
  query GetAiBotUsers {
    aiBotUsers {
      _id
      username
      profileImageUrl
      lastUnansweredMessageDate
    }
  }
`;

const GET_CHATS = gql`
  query GetChats($paging: PagingArgs!, $filter: ChatFilterInput) {
    chats(paging: $paging, filter: $filter) {
      paging {
        total
        page
        limit
      }
      nodes {
        _id
        updatedDate
        lastMessageId
        lastMessage {
          _id
          text
          created
          fromId
        }
        members {
          _id
          username
          email
          admin
          deleted
          premium
          profilePhoto {
            url
            thumbnailUrl
          }
        }
        ended
        endedDate
      }
    }
  }
`;

const GET_MESSAGES = gql`
  query GetMessages($_id: String!, $input: PagingArgs!, $userId: String) {
    chat(_id: $_id, userId: $userId) {
      messages(input: $input) {
        paging {
          total
          page
          limit
        }
        nodes {
          _id
          chatId
          fromId
          text
          created
          viewedByIds
        }
      }
    }
  }
`;

const SEND_MESSAGE = gql`
  mutation SendMessage($input: SendMessageToChatArgs!) {
    sendMessage(input: $input)
  }
`;

const VIEW_CHAT = gql`
  mutation ViewChat($chatId: String!, $userId: String) {
    viewChat(chatId: $chatId, userId: $userId)
  }
`;

// Two panes filling the <Page fill> body; each pane scrolls on its own.
const ChatContainer = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  gap: 16px;
  max-width: 100%;
`;

const Pane = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  background-color: ${(props) => props.theme.cardBg};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
`;

const ConversationList = styled(Pane)`
  width: 340px;
  flex-shrink: 0;
`;

const ChatArea = styled(Pane)`
  flex: 1;
`;

const PaneHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 10px 16px;
  border-bottom: 1px solid ${(props) => props.theme.border};
`;

const PaneTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.borderStrong};
    border-radius: 3px;
  }
`;

const ConversationItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
`;

const ConversationRow = styled.div<{ $selected: boolean; $deleted: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  opacity: ${(props) => (props.$deleted ? 0.5 : 1)};
  background-color: ${(props) =>
    props.$selected ? props.theme.surfaceActive : "transparent"};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(props) =>
      props.$selected ? props.theme.surfaceActive : props.theme.surfaceHover};
  }
`;

const ConversationBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const ConversationTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConversationName = styled.span<{ $deleted: boolean }>`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) =>
    props.$deleted ? props.theme.mutedTextColor : props.theme.textColor};
  text-decoration: ${(props) => (props.$deleted ? "line-through" : "none")};
`;

const StatusIcons = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  flex-shrink: 0;
`;

const StatusIcon = styled.span<{ $tone: "orange" | "red" }>`
  display: inline-flex;
  color: ${(props) => props.theme.colors[props.$tone]};

  svg {
    width: 14px;
    height: 14px;
  }
`;

const UnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.blue};
`;

const LastMessage = styled.span<{ $deleted: boolean; $unread: boolean }>`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: ${(props) => (props.$unread ? 500 : 400)};
  color: ${(props) =>
    props.$deleted
      ? props.theme.mutedTextColor
      : props.$unread
      ? props.theme.colors.blue
      : props.theme.secondaryTextColor};
`;

const Timestamp = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.mutedTextColor};
  font-variant-numeric: tabular-nums;
`;

const ListNote = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const ChatHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const ChatHeaderName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const ChatHeaderId = styled.span`
  font-size: 12px;
  color: ${(props) => props.theme.mutedTextColor};

  code {
    font-family: ${(props) => props.theme.monoFontFamily};
    font-size: 11.5px;
  }
`;

const MessagesContainer = styled(ScrollArea)`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
`;

const MessageGroup = styled.div<{ $isFromUser: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 70%;
  align-self: ${(props) => (props.$isFromUser ? "flex-start" : "flex-end")};
  align-items: ${(props) => (props.$isFromUser ? "flex-start" : "flex-end")};
`;

// Outgoing (admin) bubbles sit on the brand fill, so their text is always white.
const MessageBubble = styled.div<{ $isFromUser: boolean }>`
  padding: 9px 13px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.4;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background-color: ${(props) =>
    props.$isFromUser ? props.theme.surfaceRaised : props.theme.colors.primary};
  color: ${(props) => (props.$isFromUser ? props.theme.textColor : "#fff")};
  border: 1px solid
    ${(props) => (props.$isFromUser ? props.theme.border : "transparent")};
  border-bottom-left-radius: ${(props) => (props.$isFromUser ? "4px" : "16px")};
  border-bottom-right-radius: ${(props) => (props.$isFromUser ? "16px" : "4px")};
`;

const MessageTime = styled.span`
  padding: 0 4px;
  font-size: 11px;
  color: ${(props) => props.theme.mutedTextColor};
  font-variant-numeric: tabular-nums;
`;

const InputArea = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid ${(props) => props.theme.border};
`;

const CenterFill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 24px;
`;

const UserOption = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const UserOptionText = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const UserOptionName = styled.span`
  font-size: 14px;
  color: ${(props) => props.theme.textColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserOptionMeta = styled.span`
  font-size: 11px;
  color: ${(props) => props.theme.mutedTextColor};
  font-variant-numeric: tabular-nums;
`;

const ToolbarLabel = styled.span`
  font-size: 13px;
  color: ${(props) => props.theme.secondaryTextColor};
`;

interface AiBotUser {
  _id: string;
  username?: string;
  profileImageUrl?: string;
  lastUnansweredMessageDate?: string;
}

interface Chat {
  _id: string;
  updatedDate: string;
  lastMessageId?: string;
  lastMessage?: {
    _id: string;
    text: string;
    created: string;
    fromId: string;
  };
  members: Array<{
    _id: string;
    username?: string;
    email?: string;
    admin?: boolean;
    deleted?: boolean;
    premium?: boolean;
    profilePhoto?: {
      url: string;
      thumbnailUrl: string;
    };
  }>;
  ended: boolean;
  endedDate?: string;
}

interface ChatMessage {
  _id: string;
  chatId: string;
  fromId: string;
  text: string;
  created: string;
  viewedByIds?: string[];
}

// Predefined messages for support (Minglify)
const SUPPORT_MESSAGES = [
  // Greetings
  "Hello! How can we help you today?",
  "Hi there! Thanks for reaching out. How can I assist you?",
  "Good to hear from you! What can I help you with?",

  // Common responses
  "I understand your concern. Let me look into this for you.",
  "Thank you for bringing this to our attention.",
  "I apologize for any inconvenience this may have caused.",
  "Let me check that for you right away.",
  "I'll need to investigate this further and get back to you.",

  // Account/Technical issues
  "Can you please provide more details about the issue you're experiencing?",
  "Have you tried logging out and logging back in?",
  "Could you please share a screenshot of what you're seeing?",
  "What device and browser are you using?",
  "When did you first notice this issue?",

  // Premium subscription
  "Premium includes: ✨ No ads • New & popular profiles • More votes • See who likes you • 1 Hot Like daily • Longer videos • Increased distance • Online status • Chat typing indicator & read receipts • Premium filters • Location updates • AI chat & photo coach • Incognito mode • Change/reset likes & votes • Profile visits • Travel plans • Free meet ideas • Recent active • User search • Find popular & active members",

  // Resolution messages
  "The issue should be resolved now. Please try again.",
  "I've updated your account. The changes should take effect immediately.",
  "This has been fixed. Please let me know if you experience any other issues.",
  "I've escalated this to our technical team for further investigation.",

  // Closing messages
  "Is there anything else I can help you with today?",
  "Thank you for your patience. Have a great day!",
  "Feel free to reach out if you need any further assistance.",
  "Glad I could help! Don't hesitate to contact us again if needed.",
];

// Predefined messages for AI bots (dating context)
const DATING_MESSAGES = [
  // Greetings & Openers
  "Hey! How's your day going?",
  "Hi there! What are you up to?",
  "Hey! How was your weekend?",
  "Hello! Nice to hear from you 😊",
  "Hi! What's been keeping you busy lately?",
  
  // Getting to know
  "What do you like to do for fun?",
  "Tell me something interesting about yourself",
  "What kind of music are you into?",
  "Do you have any fun plans for the week?",
  "What's your favorite way to spend a weekend?",
  "Are you more of a morning person or night owl?",
  "What's something you're passionate about?",
  
  // Showing interest
  "That sounds really interesting!",
  "I'd love to hear more about that",
  "You seem really passionate about that, it's great!",
  "That's so cool! How did you get into that?",
  "Wow, I've always wanted to try that",
  
  // Casual responses
  "Haha that's funny 😄",
  "Oh really? That's awesome",
  "I totally get that",
  "That makes sense",
  "Good to know!",
  "Sounds like fun!",
  "That's a great idea",
  
  // Keeping conversation going
  "What else do you enjoy doing?",
  "Have you always been interested in that?",
  "That's interesting, tell me more",
  "How long have you been doing that?",
  "What got you started with that?",
  
  // Light & playful
  "You're making me smile 😊",
  "I like your sense of humor",
  "You seem really easy to talk to",
  "This is a fun conversation!",
  "I'm enjoying getting to know you",
  
  // Suggesting next steps
  "Would love to continue this chat when you have time",
  "Let me know when you're free to chat more",
  "Looking forward to hearing from you",
  "Feel free to message me anytime",
  "Hope to talk again soon!"
];

const MINGLIFY_USER_ID = "6465b5a6689e9605660f090a";


export const Chat: React.FC = () => {
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [predefinedDialogOpen, setPredefinedDialogOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] =
    useState<string>(MINGLIFY_USER_ID);

  // Filter states
  const [requireResponse, setRequireResponse] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [pageLimit, setPageLimit] = useState(20);

  const { data: aiBotUsersData, refetch: refetchAiBotUsers } = useQuery(GET_AI_BOT_USERS, {
    fetchPolicy: "network-only",
    pollInterval: 30000, // Poll every 30 seconds to get updates
  });

  const { data: chatsData, refetch: refetchChats } = useQuery(GET_CHATS, {
    variables: {
      paging: {
        type: "page",
        pageArgs: { page: 1, limit: pageLimit },
      },
      filter: {
        requireResponse,
        showDeleted,
        userId: selectedUserId,
      },
    },
    pollInterval: 5000,
  });

  const { data: messagesData, refetch: refetchMessages } = useQuery(
    GET_MESSAGES,
    {
      variables: {
        _id: selectedChatId,
        input: {
          type: "page",
          pageArgs: { page: 1, limit: 100 },
        },
        userId: selectedUserId,
      },
      skip: !selectedChatId,
      fetchPolicy: "cache-and-network", // Always refetch when variables change
      pollInterval: selectedChatId ? 3000 : undefined, // Only poll when a chat is selected
    }
  );

  const [sendMessage] = useMutation(SEND_MESSAGE, {
    onCompleted: () => {
      setMessage("");
      refetchMessages();
      refetchChats();
      setShouldAutoScroll(true); // Auto-scroll after sending a message
    },
  });

  const [viewChat] = useMutation(VIEW_CHAT, {
    onCompleted: () => {
      // Refetch chats to update unread indicators
      refetchChats();
    },
  });

  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);

  useEffect(() => {
    // Only auto-scroll when new messages are added to the current conversation
    // Not when switching to a different chat
    if (messagesEndRef.current && shouldAutoScroll) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      setShouldAutoScroll(false);
    }
  }, [messagesData, shouldAutoScroll]);

  const handleSendMessage = () => {
    if (message.trim() && selectedChatId) {
      sendMessage({
        variables: {
          input: {
            chatId: selectedChatId,
            text: message.trim(),
            userId: selectedUserId,
          },
        },
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleChatSelection = (chatId: string) => {
    setSelectedChatId(chatId);

    // Mark chat as viewed by admin
    viewChat({
      variables: { chatId, userId: selectedUserId },
    });
  };

  const handlePredefinedMessageSelect = (predefinedMessage: string) => {
    setMessage(predefinedMessage);
    setPredefinedDialogOpen(false);
  };

  const chats: Chat[] = chatsData?.chats?.nodes || [];
  const messages: ChatMessage[] = messagesData?.chat?.messages?.nodes || [];

  // Check if selected chat is still in the filtered list
  useEffect(() => {
    if (selectedChatId && chats.length > 0) {
      const chatStillExists = chats.some((chat) => chat._id === selectedChatId);
      if (!chatStillExists) {
        // Either clear selection or keep showing the chat even if filtered out
        // Option 1: Clear selection
        setSelectedChatId(null);
        // Option 2: You could also auto-select the first chat in the list
        // if (chats.length > 0) {
        //   setSelectedChatId(chats[0]._id);
        // }
      }
    }
  }, [chats, selectedChatId]);

  const getUnreadCount = (chat: Chat) => {
    // For now return 0, could be calculated based on viewedByIds
    return 0;
  };

  const hasUnreadUserMessage = (chat: Chat) => {
    // Check if the last message needs a response from the selected user
    if (!chat.lastMessage) return false;

    // Check if the last message is from the other user (not from the selected user)
    const isLastMessageFromOther = chat.lastMessage.fromId !== selectedUserId;

    if (!isLastMessageFromOther) return false; // Last message is from selected user, no unread indicator needed

    // If we have messages data for this chat, check if the selected user has viewed the last message
    if (selectedChatId === chat._id && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      const isLastFromOther = lastMessage.fromId !== selectedUserId;

      if (!isLastFromOther) return false;

      // Check if the selected user has viewed this message
      const hasSelectedUserViewed =
        lastMessage.viewedByIds?.includes(selectedUserId);

      return !hasSelectedUserViewed;
    }

    // Fallback: assume messages from others are unread if we don't have detailed message data
    return isLastMessageFromOther;
  };

  const getOtherMember = (chat: Chat) => {
    // When we're viewing chats as a specific user (Minglify or AI bot),
    // we want to see who they're chatting with
    const otherMember = chat.members.find((member) => {
      // Find the member who is NOT the selected user
      return member._id !== selectedUserId;
    });

    // Fallback to first member if not found
    return otherMember || chat.members[0];
  };

  const sortedAiBotUsers: AiBotUser[] =
    aiBotUsersData?.aiBotUsers
      ?.slice() // Create a copy to avoid mutating original
      ?.sort((a: AiBotUser, b: AiBotUser) => {
        // Sort by lastUnansweredMessageDate, most recent first
        if (!a.lastUnansweredMessageDate && !b.lastUnansweredMessageDate)
          return 0;
        if (!a.lastUnansweredMessageDate) return 1;
        if (!b.lastUnansweredMessageDate) return -1;
        return (
          new Date(b.lastUnansweredMessageDate).getTime() -
          new Date(a.lastUnansweredMessageDate).getTime()
        );
      }) ?? [];

  const quickReplies =
    selectedUserId === MINGLIFY_USER_ID ? SUPPORT_MESSAGES : DATING_MESSAGES;

  return (
    <Page
      title="Admin chat"
      fill
      actions={
        <>
          {/* User Selector */}
          <Select
            size="small"
            value={selectedUserId}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setSelectedChatId(null); // Clear selected chat when switching users
            }}
            aria-label="Chat as"
            sx={{
              width: 280,
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: 0.75,
              },
            }}
          >
            <MenuItem value={MINGLIFY_USER_ID}>
              <UserOption>
                <Avatar sx={{ width: 24, height: 24 }} src="">
                  M
                </Avatar>
                <UserOptionText>
                  <UserOptionName>Minglify (Support)</UserOptionName>
                </UserOptionText>
              </UserOption>
            </MenuItem>
            {sortedAiBotUsers.map((bot) => (
              <MenuItem key={bot._id} value={bot._id}>
                <UserOption>
                  <Avatar
                    sx={{ width: 24, height: 24 }}
                    src={bot.profileImageUrl}
                  >
                    {bot.username?.charAt(0).toUpperCase()}
                  </Avatar>
                  <UserOptionText>
                    <UserOptionName>{bot.username}</UserOptionName>
                    {bot.lastUnansweredMessageDate && (
                      <UserOptionMeta>
                        {format(
                          new Date(bot.lastUnansweredMessageDate),
                          "MMM d, h:mm a"
                        )}
                      </UserOptionMeta>
                    )}
                  </UserOptionText>
                </UserOption>
              </MenuItem>
            ))}
          </Select>
          <Tooltip title="Refresh AI bot list">
            <IconButton
              size="small"
              onClick={() => refetchAiBotUsers()}
              aria-label="Refresh AI bot list"
            >
              <ArrowPathIcon width={18} />
            </IconButton>
          </Tooltip>
        </>
      }
      toolbar={
        <>
          {/* Filter Controls */}
          <FilterChip
            label="Require response"
            selected={requireResponse}
            onChange={setRequireResponse}
          />
          <FilterChip
            label="Show deleted"
            selected={showDeleted}
            onChange={setShowDeleted}
          />
          <ToolbarLabel>Show</ToolbarLabel>
          <Select
            size="small"
            value={pageLimit}
            onChange={(e) => setPageLimit(Number(e.target.value))}
            aria-label="Number of chats"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value={10}>10 chats</MenuItem>
            <MenuItem value={20}>20 chats</MenuItem>
            <MenuItem value={50}>50 chats</MenuItem>
            <MenuItem value={100}>100 chats</MenuItem>
          </Select>
        </>
      }
    >
      <ChatContainer>
        <ConversationList>
          <PaneHeader>
            <PaneTitle>Conversations</PaneTitle>
            {(requireResponse || showDeleted) && <Badge>Filtered</Badge>}
          </PaneHeader>
          <ScrollArea>
            {chatsData && chats.length === 0 && (
              <ListNote>No conversations match these filters.</ListNote>
            )}
            <ConversationItems>
              {chats.map((chat) => {
                const otherMember = getOtherMember(chat);
                const unreadCount = getUnreadCount(chat);
                const hasUnread = hasUnreadUserMessage(chat);
                const isDeleted = !!otherMember?.deleted;

                return (
                  <ConversationRow
                    key={chat._id}
                    $selected={selectedChatId === chat._id}
                    $deleted={isDeleted}
                    onClick={() => handleChatSelection(chat._id)}
                  >
                    <MuiBadge badgeContent={unreadCount} color="primary">
                      <Avatar
                        src={
                          otherMember?.profilePhoto?.thumbnailUrl ||
                          otherMember?.profilePhoto?.url
                        }
                        sx={{
                          filter: isDeleted ? "grayscale(100%)" : "none",
                        }}
                      >
                        {(otherMember?.username || "U").charAt(0).toUpperCase()}
                      </Avatar>
                    </MuiBadge>
                    <ConversationBody>
                      <ConversationTop>
                        <ConversationName $deleted={isDeleted}>
                          {otherMember?.username || "Unknown User"}
                        </ConversationName>

                        {/* User status indicators */}
                        <StatusIcons>
                          {otherMember?.premium && (
                            <StatusIcon $tone="orange" title="Premium">
                              <StarIcon />
                            </StatusIcon>
                          )}
                          {isDeleted && (
                            <StatusIcon $tone="red" title="Deleted user">
                              <TrashIcon />
                            </StatusIcon>
                          )}
                          {hasUnread && <UnreadDot title="Needs response" />}
                        </StatusIcons>
                      </ConversationTop>
                      <LastMessage $deleted={isDeleted} $unread={hasUnread}>
                        {chat.lastMessage?.text || "No messages yet"}
                      </LastMessage>
                      <Timestamp>
                        {format(new Date(chat.updatedDate), "MMM d, h:mm a")}
                      </Timestamp>
                    </ConversationBody>
                  </ConversationRow>
                );
              })}
            </ConversationItems>
          </ScrollArea>
        </ConversationList>

        <ChatArea>
          {selectedChatId ? (
            <>
              <PaneHeader>
                {(() => {
                  const selectedChat = chats.find(
                    (c) => c._id === selectedChatId
                  );
                  const otherMember = selectedChat
                    ? getOtherMember(selectedChat)
                    : null;

                  return (
                    <>
                      <Avatar
                        sx={{ width: 36, height: 36 }}
                        src={
                          otherMember?.profilePhoto?.thumbnailUrl ||
                          otherMember?.profilePhoto?.url
                        }
                      >
                        {(otherMember?.username || "U").charAt(0).toUpperCase()}
                      </Avatar>
                      <ChatHeaderText>
                        <ChatHeaderName>
                          {otherMember?.username || "Unknown User"}
                        </ChatHeaderName>
                        <ChatHeaderId>
                          User ID: <code>{otherMember?._id}</code>
                        </ChatHeaderId>
                      </ChatHeaderText>
                    </>
                  );
                })()}
              </PaneHeader>

              <MessagesContainer>
                {[...messages].reverse().map((msg) => {
                  // Check if the message is from the other member (not the selected user)
                  const isFromOtherUser = msg.fromId !== selectedUserId;

                  return (
                    <MessageGroup key={msg._id} $isFromUser={isFromOtherUser}>
                      <MessageBubble $isFromUser={isFromOtherUser}>
                        {msg.text}
                      </MessageBubble>
                      <MessageTime>
                        {format(new Date(msg.created), "h:mm a")}
                      </MessageTime>
                    </MessageGroup>
                  );
                })}
                <div ref={messagesEndRef} />
              </MessagesContainer>

              <InputArea>
                <Tooltip title="Quick replies">
                  <IconButton
                    color="primary"
                    onClick={() => setPredefinedDialogOpen(true)}
                    aria-label="Quick replies"
                    sx={{ mb: 0.25 }}
                  >
                    <BoltIcon width={20} />
                  </IconButton>
                </Tooltip>
                <TextField
                  fullWidth
                  multiline
                  maxRows={4}
                  size="small"
                  placeholder="Type your message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  variant="outlined"
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!message.trim()}
                  endIcon={<PaperAirplaneIcon width={16} />}
                  sx={{ flexShrink: 0, height: 40 }}
                >
                  Send
                </Button>
              </InputArea>
            </>
          ) : (
            <CenterFill>
              <EmptyState
                Icon={ChatBubbleLeftRightIcon}
                title="No conversation selected"
                description="Select a conversation to start chatting."
              />
            </CenterFill>
          )}
        </ChatArea>
      </ChatContainer>

      {/* Predefined Messages Dialog */}
      <Dialog
        open={predefinedDialogOpen}
        onClose={() => setPredefinedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Quick replies</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
              {quickReplies.map((msg: string, index: number) => (
                <ListItemButton
                  key={index}
                  onClick={() => handlePredefinedMessageSelect(msg)}
                  divider={index < quickReplies.length - 1}
                  sx={{ py: 1.5, px: 3 }}
                >
                  <ListItemText
                    primary={msg}
                    primaryTypographyProps={{
                      fontSize: 13,
                      lineHeight: 1.4,
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
        </DialogContent>
      </Dialog>
    </Page>
  );
};
